import { useQuery } from "@tanstack/react-query";
import { TaxLayout } from "../../components/tax/TaxLayout.js";
import {
  Card,
  EmptyState,
  Pill,
  StatCard,
} from "../../components/design-system/index.js";
import { listTaxProjections } from "../../lib/api-modules/tax.js";
import { usePreferences } from "../../lib/preferences.js";

/**
 * /tax/quarterly — list of computed quarterly projections (most-recent
 * first). The `quarterly-tax-projection` job (jobs-stack) writes a row
 * 4× per year. This page reads them via `GET /v1/tax-projections`.
 *
 * v1: read-only display. Phase 7 Session 2 may add an "edit prior-year
 * fields" form so the user can populate `prior_year_tax_cents` +
 * `prior_year_agi_cents` for the safe-harbor calculation.
 */

function formatCents(cents: number, hide: boolean): string {
  if (hide) return "••••";
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pillToneFor(status: string): "neutral" | "success" | "warning" | "danger" {
  switch (status) {
    case "110_pct":
    case "100_pct":
      return "success";
    case "90_pct":
      return "warning";
    case "not_safe":
      return "danger";
    default:
      return "neutral";
  }
}

function humanStatus(s: string): string {
  switch (s) {
    case "110_pct":
      return "Meets 110% prior-year safe harbor";
    case "100_pct":
      return "Meets 100% prior-year safe harbor";
    case "90_pct":
      return "Meets 90% current-year safe harbor";
    case "not_safe":
      return "Not meeting safe harbor";
    default:
      return s;
  }
}

export function TaxQuarterlyPage() {
  const { privacyMode } = usePreferences();
  const hide = privacyMode === true;
  const projections = useQuery({
    queryKey: ["tax-projections"],
    queryFn: () => listTaxProjections(),
  });

  return (
    <TaxLayout
      title="Quarterly tax projection"
      subtitle="Federal + NY + NYC liability projected from year-to-date income. Safe-harbor signal each quarter."
    >
      {projections.isPending ? (
        <Card className="p-6 text-sm text-[var(--color-muted)]">Loading…</Card>
      ) : projections.isError ? (
        <Card className="p-6 text-sm text-[var(--color-danger)]">
          Failed to load projections.
        </Card>
      ) : projections.data && projections.data.tax_projections.length > 0 ? (
        <div className="space-y-4">
          {projections.data.tax_projections.map((p) => (
            <Card key={p.projection_id} className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm text-[var(--color-muted)]">
                    {p.tax_year} · {p.quarter}
                  </div>
                  <div className="text-2xl font-semibold text-[var(--color-text)]">
                    {formatCents(p.projected_total_liability_cents, hide)}
                  </div>
                </div>
                <Pill tone={pillToneFor(p.safe_harbor_status)}>
                  {humanStatus(p.safe_harbor_status)}
                </Pill>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <StatCard
                  label="Federal"
                  value={formatCents(p.projected_federal_liability_cents, hide)}
                />
                <StatCard
                  label="NY State"
                  value={formatCents(p.projected_ny_liability_cents, hide)}
                />
                <StatCard
                  label="NYC"
                  value={formatCents(p.projected_nyc_liability_cents, hide)}
                />
              </div>
              <div className="mt-3 text-xs text-[var(--color-muted)]">
                Composite marginal {p.composite_marginal_rate_pct.toFixed(2)}% · effective {p.composite_effective_rate_pct.toFixed(2)}%
              </div>
              <div className="mt-1 text-xs text-[var(--color-muted)]">
                Computed {new Date(p.computed_at).toLocaleString()}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No projections yet"
          description="The quarterly-tax-projection job runs Apr 1 / Jun 1 / Sep 1 / Jan 1 at 10am ET. After the next firing, your projection appears here."
        />
      )}
    </TaxLayout>
  );
}
